/**
 * Closure banner (spec §9, patch E3).
 *
 * Shows on every project-detail tab when the project is closed.
 * Explains the role-specific implications:
 *
 *   - Assistant: read-only across the board. The role gate hides edit
 *     affordances; without the banner, the reason for the silence is
 *     opaque.
 *   - Admin: retains edit access (spec §9). The banner makes that
 *     explicit so the Admin doesn't assume the UI is broken when their
 *     edit affordances remain active.
 *
 * The banner is informational (warning tone — closure is meaningful but
 * not a problem) and persistent — closure is a long-lived state, not a
 * transient event, so we don't offer a dismiss.
 *
 * Reopening is the only "fix" for closure, and that affordance already
 * lives in the page header. The banner doesn't duplicate it.
 */
import { Alert } from '@/components/ui/alert';
import { useCurrentUser } from '@/lib/auth';
import type { Project } from '@/lib/project-queries';
import { closureBannerMessage } from './closure-banner-message';

export { closureBannerMessage } from './closure-banner-message';

interface Props {
  project: Project;
}

export function ClosureBanner({ project }: Props) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';

  if (project.lifecycleState !== 'CLOSED') return null;
  // Wait for the role to load — without it we can't choose the right
  // message. The badge in the page header already communicates "closed"
  // visually, so showing a generic banner here would be redundant.
  if (!me.data) return null;

  return (
    <div className="mb-4">
      <Alert tone="warning" title="Project closed">
        {closureBannerMessage(isAdmin)}
      </Alert>
    </div>
  );
}
